import { Container } from 'hypo-container';
import { BusinessUnitIdParser, DateFormatter, NumberFormatter } from '@estee/elc-formatters';
import { HttpTransport } from '@estee/elc-http-transport';
import { IAppConfig } from '@estee/elc-universal-utils';
import { ELCLogger } from '@estee/elc-logging';

import { LoyaltyApiSdk } from '~api/LoyaltyApiSdk';
import { QueryFragments } from '~api/QueryFragments';
import { LoyaltyRpcAPI } from '~api/RpcAPI/LoyaltyRpcAPI';
import { IPGLoyaltyApiConfig } from '~api/interfaces/ILoyaltyRpcAPI';

import { Config } from '~domain/entities/Config';
import LoyaltyInfo from '~domain/entities/LoyaltyInfo';
import { IServiceAppConfig } from '~domain/entities/IConfig';
import LoyaltyTierController from '~exported/loyalty-tier/LoyaltyTierController';

import LoyaltyPointsInfoController from '~exported/loyalty-points-info/LoyaltyPointsInfoController';
import LoyaltyDashboardController from '~exported/loyalty-dashboard/LoyaltyDashboardController';
import LoyaltyTierInfoController from '~exported/loyalty-tier-info/LoyaltyTierInfoController';
import LoyaltySubmitAReceiptController from '~exported/loyalty-submit-a-receipt/LoyaltySubmitAReceiptController';
import LoyaltyPointsHistoryController from '~exported/loyalty-points-history/LoyaltyPointsHistoryController';
import LoyaltyEarnPointsController from '~exported/loyalty-earn-points/LoyaltyEarnPointsController';
import LoyaltyBenefitsController from '~exported/loyalty-benefits/LoyaltyBenefitsController';
import LoyaltyBirthdayController from '~exported/loyalty-birthday/LoyaltyBirthdayController';
import LoyaltyRewardsController from '~exported/loyalty-rewards/LoyaltyRewardsController';
import LoyaltyMarketingPageController from '~exported/loyalty-marketing-page/LoyaltyMarketingPageController';
import LoyaltyLandingPageController from '~exported/loyalty-landing-page/LoyaltyLandingPageController';
import LoyaltyMarketingPointBenefitController from '~exported/loyalty-marketing-point-benefit/LoyaltyMarketingPointBenefitController';

import { ConfigRepository } from '~repositories/ConfigRepository';
import { LoyaltyRepository } from '~repositories/LoyaltyRepository';
import { AuthenticatedRepository } from '~repositories/AuthenticatedRepository';
import { LoyaltyServiceStartedUseCase } from '~use-cases/LoyaltyServiceStartedUseCase';

import serviceNames, { LoyaltyService } from './LoyaltyService';
import { ServiceBusController } from './ServiceBusController';
import { ISiteConfig } from '~domain/entities/ISiteConfig';

export const diContainer = new Container();
const { name } = __serviceInfo__;

export const setConfig = (configJSON: IAppConfig) => {
    diContainer.config = configJSON;
};

async function serviceInitializer(config: IServiceAppConfig) {
    setConfig(config);
    const requestedBuidConfigs = ['brand.id', 'language', 'region'];

    const useCase = new LoyaltyServiceStartedUseCase({
        appConfig: config,
        requestedBuidConfigs
    });

    await useCase.execute();
}

diContainer
    .register(serviceNames.config, (c) => new Config(c.config))
    .register(serviceNames.siteConfig, async () => {
        const loyaltyConfig = (window?.site as ISiteConfig)?.loyaltyConfig?.loyalty;
        const loyaltyConfigFile = await import(`../config/${loyaltyConfig.domain}-loyalty.json`);

        return loyaltyConfigFile;
    })
    .register(serviceNames.logger, () => new ELCLogger({ serviceName: name }))
    .register(serviceNames.loyaltyService, () => new LoyaltyService(serviceInitializer))
    .register(
        serviceNames.businessUnitIdParser,
        diContainer.factory((c) => {
            return new BusinessUnitIdParser(c.config?.businessUnitId || '2-xx-us-en-ecommv1');
        })
    )
    .register(serviceNames.loyaltyDashboardViewController, (c) => {
        const repository = c.get(serviceNames.loyaltyRepository);

        return new LoyaltyDashboardController(repository);
    })
    .register(serviceNames.httpTransport, () => new HttpTransport())
    .register(serviceNames.loyaltyRpcAPI, (c) => {
        const payload: IPGLoyaltyApiConfig = {
            config: {
                httpTransport: c.get(serviceNames.httpTransport),
                businessUnitId: c.get(serviceNames.config).businessUnitId
            },
            serviceName: name,
            serviceVersion: ''
        };

        return new LoyaltyRpcAPI(payload);
    })
    .register(serviceNames.loyaltyInfo, () => new LoyaltyInfo())
    .register(serviceNames.configRepository, (c) => {
        const entity = c.get(serviceNames.config);

        return new ConfigRepository(entity);
    })
    .register(serviceNames.authenticatedRepository, (c) => {
        const api = c.get(serviceNames.loyaltyApiSdk);

        return new AuthenticatedRepository(api);
    })
    .register(serviceNames.loyaltyRepository, (c) => {
        const api = c.get(serviceNames.loyaltyApiSdk);
        const logger = c.get(serviceNames.logger);
        const rpcApi = c.get(serviceNames.loyaltyRpcAPI);

        return new LoyaltyRepository(api, logger, rpcApi);
    })
    .register(serviceNames.loyaltyTierViewController, (c) => {
        const repository = c.get(serviceNames.loyaltyRepository);

        return new LoyaltyTierController(repository);
    })
    .register(serviceNames.loyaltySubmitAReceiptController, (c) => {
        const repository = c.get(serviceNames.loyaltyRepository);

        return new LoyaltySubmitAReceiptController(repository);
    })
    .register(serviceNames.loyaltyPointsHistoryController, (c) => {
        const repository = c.get(serviceNames.loyaltyRepository);

        return new LoyaltyPointsHistoryController(repository);
    })
    .register(serviceNames.loyaltyEarnPointsController, (c) => new LoyaltyEarnPointsController())
    .register(
        serviceNames.loyaltyBenefitsController,
        (c) => new LoyaltyBenefitsController(c.get(serviceNames.loyaltyRepository))
    )
    .register(
        serviceNames.loyaltyRewardsController,
        (c) => new LoyaltyRewardsController(c.get(serviceNames.loyaltyRepository))
    )
    .register(
        serviceNames.loyaltyPointBenefitController,
        diContainer.factory(
            (c) =>
                new LoyaltyMarketingPointBenefitController({
                    configs: c.get(serviceNames.configRepository),
                    loyaltyRepository: c.get(serviceNames.loyaltyRepository),
                    numberFormatter: c.get(serviceNames.numberFormatter)
                })
        )
    )
    .register(
        serviceNames.loyaltyBirthdayController,
        (c) => new LoyaltyBirthdayController(c.get(serviceNames.loyaltyRepository))
    )
    .register(
        serviceNames.loyaltyTierInfoController,
        diContainer.factory(
            (c) =>
                new LoyaltyTierInfoController({
                    loyaltyRepository: c.get(serviceNames.loyaltyRepository),
                    numberFormatter: c.get(serviceNames.numberFormatter)
                })
        )
    )
    .register(
        serviceNames.loyaltyPointsInfoController,
        diContainer.factory((c) => {
            const { locale } = c.get(serviceNames.businessUnitIdParser);

            return new LoyaltyPointsInfoController({
                loyaltyRepository: c.get(serviceNames.loyaltyRepository),
                dateFormatter: c.get(serviceNames.dateFormatter),
                numberFormatter: c.get(serviceNames.numberFormatter),
                locale: locale
            });
        })
    )
    .register(serviceNames.serviceBusController, (c) => new ServiceBusController())
    .register(serviceNames.loyaltyApiSdk, (c) => {
        const transport = c.get(serviceNames.httpTransport);

        return new LoyaltyApiSdk(transport);
    })
    .register(
        serviceNames.queryFragments,
        diContainer.factory(() => new QueryFragments())
    )
    .register(
        serviceNames.dateFormatter,
        diContainer.factory((c) => {
            const { locale } = c.get(serviceNames.businessUnitIdParser);

            const dateFormatterConfig = { locale };

            return new DateFormatter(dateFormatterConfig);
        })
    )
    .register(
        serviceNames.numberFormatter,
        diContainer.factory((c) => {
            const { locale } = c.get(serviceNames.businessUnitIdParser);

            const numberFormatterConfig = { locale };

            return new NumberFormatter(numberFormatterConfig);
        })
    )
    .register(
        serviceNames.loyaltyMarketingPageController,
        diContainer.factory(
            (c) =>
                new LoyaltyMarketingPageController({
                    loyaltyRepository: c.get(serviceNames.loyaltyRepository)
                })
        )
    )
    .register(
        serviceNames.loyaltyLandingPageController,
        diContainer.factory(
            (c) =>
                new LoyaltyLandingPageController({
                    loyaltyRepository: c.get(serviceNames.loyaltyRepository)
                })
        )
    );
